import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { toast } from 'sonner';

import { readRefreshToken, writeRefreshToken } from './storage';
import { useAuthStore } from '../stores/auth-store';

type RedirectFn = (path: string) => void;

let redirectTo: RedirectFn = (path) => {
  if (typeof window !== 'undefined' && window.location.pathname !== path) {
    window.location.assign(path);
  }
};

function normalizeApiBase(url: string): string {
  const trimmed = url.replace(/\/+$/, '');
  return trimmed.endsWith('/v1') ? trimmed : `${trimmed}/v1`;
}

export const api = axios.create({
  baseURL: normalizeApiBase(import.meta.env['VITE_API_BASE_URL'] ?? 'http://localhost:3001'),
});

export const analyticsApi = axios.create({
  baseURL: normalizeApiBase(import.meta.env['VITE_ANALYTICS_API_BASE_URL'] ?? 'http://localhost:3004'),
});

export function setApiRedirect(handler: RedirectFn): void {
  redirectTo = handler;
}

export function initApiClient(): void {
  const attachAuth = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const token = useAuthStore.getState().token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  };

  const handleError = (error: AxiosError): Promise<never> => {
    const status = error.response?.status;

    if (status === 401) {
      useAuthStore.getState().clearAuth();
      redirectTo('/login');
    } else if (status === 429) {
      toast.error('Rate limit reached. Please wait before trying again.');
    }

    return Promise.reject(error);
  };

  api.interceptors.request.use(attachAuth);
  analyticsApi.interceptors.request.use(attachAuth);

  api.interceptors.response.use(
    (response) => response,
    handleError,
  );
  analyticsApi.interceptors.response.use(
    (response) => response,
    handleError,
  );
}

export async function refreshAccessToken(): Promise<string> {
  const refreshToken = readRefreshToken();
  if (!refreshToken) {
    throw new Error('Missing refresh token');
  }

  const response = await api.post<{ accessToken: string; refreshToken: string }>('/auth/refresh', {
    refreshToken,
  });

  const { accessToken, refreshToken: nextRefreshToken } = response.data;
  writeRefreshToken(nextRefreshToken);
  useAuthStore.setState((state) => ({ ...state, token: accessToken, refreshToken: nextRefreshToken }));
  return accessToken;
}
